import React, { useState, useMemo } from 'react';
import {
  WIETZ_TATBESTAENDE,
  WIETZ_KATEGORIEN,
  type WietzKategorie,
  type WietzTatbestand,
} from '../data/tatbestaendeWietz';

const KATEGORIE_COLORS: Record<WietzKategorie, string> = {
  Tierhaltung:  'bg-amber-100  text-amber-800  dark:bg-amber-950/30  dark:text-amber-300',
  Labor:        'bg-purple-100 text-purple-800 dark:bg-purple-950/30 dark:text-purple-300',
  Gesundheit:   'bg-blue-100   text-blue-800   dark:bg-blue-950/30   dark:text-blue-300',
  Abfall:       'bg-gray-100   text-gray-700   dark:bg-gray-800      dark:text-gray-300',
  Landwirtschaft:'bg-green-100 text-green-800  dark:bg-green-950/30  dark:text-green-300',
  Sonstige:     'bg-pink-100   text-pink-800   dark:bg-pink-950/30   dark:text-pink-300',
};

function DetailModal({ t, onClose }: { t: WietzTatbestand; onClose: () => void }): React.JSX.Element {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={e => { if (e.target === e.currentTarget) { onClose(); } }}>
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-3">
            <span className="font-mono text-xs font-bold text-gray-400">{t.code}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${KATEGORIE_COLORS[t.kategorie]}`}>
              {t.kategorie}
            </span>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400">
            ✕
          </button>
        </div>
        <div className="p-6 space-y-4">
          <h2 className="font-semibold text-gray-900 dark:text-gray-100 text-base leading-snug">
            {t.bezeichnung}
          </h2>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="bg-blue-50 dark:bg-blue-950/20 rounded-lg p-3">
              <p className="text-xs text-blue-500 font-medium mb-1">Untersuchungsintervall</p>
              <p className="font-bold text-blue-900 dark:text-blue-200">
                {t.intervallMon} Monate ({t.intervallMon / 12} {t.intervallMon === 12 ? 'Jahr' : 'Jahre'})
              </p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
              <p className="text-xs text-gray-500 font-medium mb-1">Rechtsgrundlage</p>
              <p className="font-medium text-gray-900 dark:text-gray-100 text-xs">{t.gesetzGrundl}</p>
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Relevante Erreger / Biologische Arbeitsstoffe
            </p>
            <div className="flex flex-wrap gap-1.5">
              {t.erreger.map(e => (
                <span key={e}
                  className="text-xs bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-400 px-2 py-0.5 rounded-full border border-red-100 dark:border-red-900/50">
                  {e}
                </span>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Pflichtdokumentation
            </p>
            <div className="flex flex-wrap gap-1.5">
              {t.pflichtDoku.map(d => (
                <span key={d}
                  className="text-xs bg-green-50 text-green-700 dark:bg-green-950/30 dark:text-green-400 px-2 py-0.5 rounded-full">
                  ✓ {d}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function WietzTatbestandEditor(): React.JSX.Element {
  const [search, setSearch]         = useState('');
  const [filterKat, setFilterKat]   = useState<WietzKategorie | 'Alle'>('Alle');
  const [detail, setDetail]         = useState<WietzTatbestand | null>(null);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return WIETZ_TATBESTAENDE.filter(t => {
      const matchKat = filterKat === 'Alle' || t.kategorie === filterKat;
      const matchQ   = q === '' ||
        t.code.toLowerCase().includes(q) ||
        t.bezeichnung.toLowerCase().includes(q) ||
        t.erreger.some(e => e.toLowerCase().includes(q));
      return matchKat && matchQ;
    });
  }, [search, filterKat]);

  return (
    <div className="space-y-4">
      {/* Filter-Leiste */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-48">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
          <input type="search" value={search} onChange={e => { setSearch(e.target.value); }}
            placeholder="Code, Bezeichnung oder Erreger…"
            className="input-field pl-9 text-sm" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {(['Alle', ...WIETZ_KATEGORIEN] as const).map(k => (
            <button key={k}
              onClick={() => { setFilterKat(k); }}
              className={`text-xs px-3 py-1 rounded-full font-medium transition-colors
                ${filterKat === k
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}>
              {k}
            </button>
          ))}
        </div>
      </div>

      {/* Tabelle */}
      <div className="card overflow-hidden p-0">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
            <tr>
              <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3 w-16">Code</th>
              <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">Bezeichnung</th>
              <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3 hidden md:table-cell">Kategorie</th>
              <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3 hidden lg:table-cell">Intervall</th>
              <th className="w-8 px-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="text-center py-10 text-gray-400 italic">
                Keine Tatbestände gefunden.
              </td></tr>
            )}
            {filtered.map(t => (
              <tr key={t.code}
                className="hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors"
                onClick={() => { setDetail(t); }}>
                <td className="px-4 py-3 font-mono text-xs font-bold text-blue-700 dark:text-blue-400">{t.code}</td>
                <td className="px-4 py-3 text-gray-900 dark:text-gray-100">{t.bezeichnung}</td>
                <td className="px-4 py-3 hidden md:table-cell">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${KATEGORIE_COLORS[t.kategorie]}`}>
                    {t.kategorie}
                  </span>
                </td>
                <td className="px-4 py-3 hidden lg:table-cell text-gray-500 text-xs">
                  {t.intervallMon} Mo.
                </td>
                <td className="px-2 py-3 text-gray-300 dark:text-gray-600">›</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-gray-400 text-center">
        {WIETZ_TATBESTAENDE.length} Tatbestände gem. §§ 49–52 ASchG iVm AMR 4 (Biologische Arbeitsstoffe)
      </p>

      {detail !== null && <DetailModal t={detail} onClose={() => { setDetail(null); }} />}
    </div>
  );
}
